import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Target, Sparkles, Send, ArrowRight, Check, CircleCheck, ShieldCheck,
} from 'lucide-react';

/* ----------------------------------------------------------------------------
 * For Candidates — the free, candidate side of the two-sided platform.
 * A single AI agent that applies you to real fits (quality over a 300-app
 * spray). Free acquisition wedge. Blue brand, editorial serif headings,
 * hairline borders, subtle motion. Matches Home.js / Product.js.
 * -------------------------------------------------------------------------- */

const BLUE = '#2563EB';

const reveal = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
};

const STEPS = [
  {
    icon: Target,
    title: 'Set your targets',
    desc: 'Tell the agent the roles, seniority, and locations you want. Five minutes, once.',
  },
  {
    icon: Sparkles,
    title: 'The agent finds real fits',
    desc: 'It matches and applies you to roles you can actually get interviews for — no spraying 300 applications into the void.',
  },
  {
    icon: Send,
    title: 'Get in front of startups hiring',
    desc: 'You land in a pool of motivated candidates that startups search when they have a role to fill — so the right teams find you.',
  },
];

const DIFF = [
  'Quality over a 300-application spray — the agent applies you only to real fits.',
  "You look motivated, not desperate — startups see you because you actually want the role.",
  "It's free. The candidate side always will be.",
];

const TRUST = [
  'Free.',
  'We never spam you.',
  'You control who sees your profile.',
  "We don't sell your data.",
];

function ForCandidates() {
  return (
    <div className="bg-[#F8FAFC] dark:bg-gray-950 text-gray-900 dark:text-gray-100">
      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden border-b border-gray-200 dark:border-gray-800 bg-gradient-to-b from-white to-[#F8FAFC] dark:from-gray-950 dark:to-gray-950">
        <div className="mx-auto max-w-4xl px-6 py-20 text-center lg:px-8 lg:py-28">
          <motion.div {...reveal} transition={{ duration: 0.5 }} className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.05em] text-gray-500">
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: BLUE }} />
            For candidates · Free
          </motion.div>
          <motion.h1 {...reveal} transition={{ duration: 0.55, delay: 0.05 }}
            className="mt-6 font-serif text-5xl font-semibold leading-[1.05] tracking-tight text-gray-900 dark:text-white sm:text-6xl">
            One AI agent that applies you to the jobs you'll actually get interviews for.
          </motion.h1>
          <motion.p {...reveal} transition={{ duration: 0.55, delay: 0.1 }}
            className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-gray-600 dark:text-gray-300">
            Stop spraying applications. Set your targets once and let the agent find the real fits — while you land in the pool startups search when they're hiring.
          </motion.p>
          <motion.div {...reveal} transition={{ duration: 0.55, delay: 0.16 }} className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link to="/register" className="inline-flex items-center justify-center gap-2 rounded-lg px-6 py-3 text-base font-semibold text-white transition-colors"
              style={{ background: BLUE }} onMouseEnter={(e) => (e.currentTarget.style.background = '#1D4FD8')} onMouseLeave={(e) => (e.currentTarget.style.background = BLUE)}>
              Get started free <ArrowRight size={17} />
            </Link>
          </motion.div>
          <motion.div {...reveal} transition={{ duration: 0.55, delay: 0.22 }} className="mt-8 flex flex-wrap items-center justify-center gap-x-7 gap-y-3 text-sm font-medium text-gray-600 dark:text-gray-300">
            {['Free forever', 'No spam', 'You control your profile'].map((t) => (
              <span key={t} className="inline-flex items-center gap-2">
                <Check size={16} style={{ color: BLUE }} /> {t}
              </span>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-8">
        <motion.div {...reveal} transition={{ duration: 0.5 }} className="max-w-2xl">
          <div className="text-xs font-semibold uppercase tracking-[0.06em]" style={{ color: BLUE }}>How it works</div>
          <h2 className="mt-4 font-serif text-4xl font-semibold tracking-tight text-gray-900 dark:text-white">
            Three steps, then the agent works for you.
          </h2>
        </motion.div>
        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3">
          {STEPS.map((s, i) => (
            <motion.div key={s.title} {...reveal} transition={{ duration: 0.5, delay: i * 0.08 }}
              className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-7 transition-colors hover:border-gray-300 dark:hover:border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: '#EFF4FE', color: BLUE }}>
                  <s.icon size={20} strokeWidth={1.8} />
                </div>
                <span className="font-serif text-3xl font-semibold text-gray-200 dark:text-gray-700">{i + 1}</span>
              </div>
              <h3 className="mt-5 font-serif text-lg font-semibold text-gray-900 dark:text-white">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-gray-500 dark:text-gray-400">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ===== WHY IT'S DIFFERENT ===== */}
      <section className="border-y border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-8">
          <div className="grid grid-cols-1 items-center gap-12 md:grid-cols-2 lg:gap-16">
            <motion.div {...reveal} transition={{ duration: 0.5 }}>
              <div className="text-xs font-semibold uppercase tracking-[0.06em]" style={{ color: BLUE }}>Why it's different</div>
              <h2 className="mt-4 font-serif text-4xl font-semibold tracking-tight text-gray-900 dark:text-white">
                Quality over the spray.
              </h2>
              <p className="mt-4 text-lg leading-relaxed text-gray-600 dark:text-gray-300">
                Mass-applying makes you one of 300. The agent does the opposite: fewer applications, better fits, and a profile that signals you actually want the role.
              </p>
            </motion.div>
            <motion.div {...reveal} transition={{ duration: 0.5, delay: 0.08 }}
              className="rounded-2xl border-2 bg-white dark:bg-gray-900 p-7" style={{ borderColor: '#BFDBFE' }}>
              <ul className="space-y-3.5 text-[15px] text-gray-700 dark:text-gray-200">
                {DIFF.map((t) => (
                  <li key={t} className="flex items-start gap-2.5">
                    <CircleCheck size={18} className="mt-0.5 flex-none" style={{ color: BLUE }} /> {t}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== TRUST BLOCK ===== */}
      <section className="mx-auto max-w-5xl px-6 py-24 lg:px-8">
        <motion.div {...reveal} transition={{ duration: 0.5 }}
          className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-[#F8FAFC] dark:bg-gray-900 p-8 sm:p-10">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.06em]" style={{ color: BLUE }}>
            <ShieldCheck size={15} /> Your data, your call
          </div>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {TRUST.map((t) => (
              <div key={t} className="flex items-start gap-2.5 text-[15px] font-medium text-gray-700 dark:text-gray-200">
                <Check size={18} className="mt-0.5 flex-none" style={{ color: BLUE }} /> {t}
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* ===== BOTTOM CTA ===== */}
      <section className="bg-[#0F172A]">
        <div className="mx-auto max-w-5xl px-6 py-24 text-center lg:px-8">
          <motion.div {...reveal} transition={{ duration: 0.55 }}>
            <h2 className="font-serif text-4xl font-semibold leading-tight text-white sm:text-5xl">
              Let the agent do the applying.
            </h2>
            <p className="mx-auto mt-5 max-w-2xl text-lg text-gray-300">
              Set your targets once. Get in front of startups hiring. It's free.
            </p>
            <div className="mt-9 flex justify-center">
              <Link to="/register" className="inline-flex items-center justify-center gap-2 rounded-lg px-7 py-3.5 text-base font-semibold text-white" style={{ background: BLUE }}>
                Get started free <ArrowRight size={17} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default ForCandidates;
